import { ThemeProvider } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { LoadingPage, ReemaThemeV2 } from '@act/storybook';
import { SettingsProvider } from '@act/storybook/theme/settings-drawer/SettingsProvider';
import { DisplayModeProvider } from '@act/storybook/theme/display/DisplayModeProvider';
import { getPersistor, getStore } from 'redux/reemaStore.util';
import { useGetAppConfig } from 'utilities/appConfigHook.hook';
import AppConfigLoadError from './AppConfigLoadError';
import InitialDataHelper from '../app/InitialDataHelper';
import { LanguageProvider } from 'utilities/language-provider';
import { TrackingProvider } from '../tracking/TrackingContext';
import ToastPortal from '../portals/ToastPortal';
import { ReportsAppRouter } from '../reports/ReportsAppRouter';

/**
 * Reports subdomain root — reuses the main Care Portal Redux store and auth.
 *
 * Local dev: open `http://reports.localhost:5670` (Vite accepts `*.localhost`
 * subdomains). Alternatively add `127.0.0.1 reports.localhost` to `/etc/hosts`.
 */
const ReportsAppContent = () => {
  const appConfig = useGetAppConfig();

  if (appConfig.status === 'loading') {
    return <LoadingPage />;
  }

  if (appConfig.status === 'error') {
    return <AppConfigLoadError message={appConfig.errorMessage} />;
  }

  return (
    <Provider store={getStore()}>
      <PersistGate loading={<LoadingPage />} persistor={getPersistor()}>
        <LanguageProvider>
          <InitialDataHelper />
          <SettingsProvider>
            <ThemeProvider theme={ReemaThemeV2.LIGHT_MODE}>
              <DisplayModeProvider>
                <ReportsAppRouter />
                <ToastPortal />
                <div id="toast-portal" />
              </DisplayModeProvider>
            </ThemeProvider>
          </SettingsProvider>
        </LanguageProvider>
      </PersistGate>
    </Provider>
  );
};

/**
 * Reports subdomain shell with an app-wide tracking context.
 * @returns Reports application root.
 */
const ReportsApp = () => (
  <TrackingProvider contextName="reports-app">
    <ReportsAppContent />
  </TrackingProvider>
);

export default ReportsApp;
